import React from "react";
import { useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { ForumOutlined as ChatIcon } from "@mui/icons-material";
import { ErrorBoundary } from "react-error-boundary";

import TicketsManager from "../../components/TicketsManager/";
import Ticket from "../../components/Ticket/";
import { i18n } from "../../translate/i18n";

const SectionFallback = ({ resetErrorBoundary }) => (
  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" gap={1} p={2}>
    <Typography variant="body2" color="text.secondary">Something went wrong in this section.</Typography>
    <Typography
      variant="caption"
      color="primary"
      sx={{ cursor: "pointer", textDecoration: "underline" }}
      onClick={resetErrorBoundary}
    >
      Try again
    </Typography>
  </Box>
);

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    height: "calc(100% - 48px)",
    overflow: "hidden",
    backgroundColor: theme.palette.background.default,
  },

  sidebar: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },

  sidebarCollapsed: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  chatArea: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },

  welcome: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2.5),
    padding: theme.spacing(4),
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  welcomeIconRing: {
    width: 88,
    height: 88,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));

const Chat = () => {
  const { classes } = useStyles();
  const { ticketId } = useParams();

  return (
    <div className={classes.root}>
      <Grid container sx={{ height: "100%", flex: 1 }}>
        <Grid
          size={{ xs: 12, md: 4 }}
          className={ticketId ? classes.sidebarCollapsed : classes.sidebar}
        >
          <ErrorBoundary FallbackComponent={SectionFallback}>
            <TicketsManager />
          </ErrorBoundary>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }} className={classes.chatArea}>
          {ticketId ? (
            <ErrorBoundary FallbackComponent={SectionFallback}>
              <Ticket />
            </ErrorBoundary>
          ) : (
            <Box className={classes.welcome}>
              <Box className={classes.welcomeIconRing}>
                <ChatIcon sx={{ fontSize: 42 }} />
              </Box>

              <Box sx={{ textAlign: "center", maxWidth: 360 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "text.primary",
                    mb: 0.75,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {i18n.t("chat.noTicketMessage")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", lineHeight: 1.7 }}
                >
                  Select a conversation from the panel on the left to begin.
                </Typography>
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default Chat;
